import React, { useState, useRef, useEffect } from 'react';
import { View, Text, ImageBackground, TextInput, TouchableOpacity, Animated, Platform, Easing, ActivityIndicator, Alert, Modal } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useNavigation } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { globalStyles } from '../styles/globalStyles';
import { homeStyles } from '../styles/HomeStyles';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useAnimation } from '../hooks/useAnimation';
import { colors, shadows, spacing } from '../styles/theme';
import { SearchBar } from '../components/SearchBar';
import { useTypingAnimation } from '../hooks/useTypingAnimation';
import { CommunityAuthForm } from '../components/CommunityAuthForm';
import { getResponsiveValue } from '../styles/breakpoints';

const PLACEHOLDER_PHRASES = [
  'Backline Rental', 'Los Angeles',
  'Screen Printing', 'Austin',
  'Booker', 'Kalamazoo',
  'Tour Manager', 'Chicago',
  'FOH Engineer', 'Atlanta',
];

export function CommunityScreen() { 
  const navigation = useNavigation();
  const auth = getAuth();
  const db = getFirestore();
  const currentUser = auth.currentUser;
  const [searchText, setSearchText] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const animatedPlaceholder = useTypingAnimation(PLACEHOLDER_PHRASES);
  const [gateModalVisible, setGateModalVisible] = useState(false);
  const [gateModalTitle, setGateModalTitle] = useState('');
  const [gateModalMessage, setGateModalMessage] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [userIsLoggedIn, setUserIsLoggedIn] = useState(false);
  // Create refs for animations
  const titleFadeRef = useRef(new Animated.Value(0));
  const titleSlideRef = useRef(new Animated.Value(30));
  const subtitleFadeRef = useRef(new Animated.Value(0));
  const subtitleSlideRef = useRef(new Animated.Value(30));
  const buttonsFadeRef = useRef(new Animated.Value(0));
  const buttonsSlideRef = useRef(new Animated.Value(30));
  const searchFadeRef = useRef(new Animated.Value(0));
  const searchSlideRef = useRef(new Animated.Value(30));
  const toggleSlideRef = useRef(new Animated.Value(30));
  const toggleFadeRef = useRef(new Animated.Value(0));
  const activeButtonPositionRef = useRef(new Animated.Value(1)); // 0 = left (Venues), 1 = right (Community)

  useEffect(() => {
    const startAnimations = () => {
      const DURATION = 400;
      const SEQUENCE_DELAY = 100;

      const animations = [
        Animated.parallel([
          Animated.timing(titleFadeRef.current, {
            toValue: 1,
            duration: DURATION,
            delay: 0,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          }),
          Animated.timing(titleSlideRef.current, {
            toValue: 0,
            duration: DURATION,
            delay: 0,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          })
        ]),
        Animated.parallel([
          Animated.timing(subtitleFadeRef.current, {
            toValue: 1,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          }),
          Animated.timing(subtitleSlideRef.current, {
            toValue: 0,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          })
        ]),
        Animated.parallel([
          Animated.timing(searchFadeRef.current, {
            toValue: 1,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          }),
          Animated.timing(searchSlideRef.current, {
            toValue: 0,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          })
        ]),
        Animated.parallel([
          Animated.timing(toggleFadeRef.current, {
            toValue: 1,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          }),
          Animated.timing(toggleSlideRef.current, {
            toValue: 0,
            duration: DURATION,
            delay: SEQUENCE_DELAY,
            easing: Easing.bezier(0.4, 0, 0.2, 1),
            useNativeDriver: true
          })
        ])
      ];

      Animated.sequence(animations).start(() => {
        setAnimationsComplete(true);
      });
    };

    const checkUserLogin = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        setUserIsLoggedIn(true);
      }
    };

    checkUserLogin();

    // const checkAccess = async () => {
    //   const currentUser = auth.currentUser;
    //   if (!currentUser) {
    //     setGateModalTitle('Coming Soon');
    //     setGateModalMessage('This feature is coming soon.');
    //     setGateModalVisible(true);
    //     setAccessBlocked(true);
    //     return;
    //   }
    //   try {
    //     const snap = await getDoc(doc(db, 'users', currentUser.uid));
    //     if (!snap.exists() || snap.data().userType !== 'admin') {
    //       setGateModalTitle('Coming Soon');
    //       setGateModalMessage('This feature is coming soon.');
    //       setGateModalVisible(true);
    //       setAccessBlocked(true);
    //       return;
    //     }
    //   } catch (e) {
    //     console.error('CommunityScreen gate check failed:', e);
    //     setGateModalTitle('Coming Soon');
    //     setGateModalMessage('This feature is coming soon.');
    //     setGateModalVisible(true);
    //     setAccessBlocked(true);
    //     return;
    //   }

      startAnimations();
    // };

    // checkAccess();
  }, []);

  const handleSearch = () => {
    if (searchText.trim()) {
      // Navigate to community search results
      navigation.navigate('CommunityList', { 
        searchQuery: searchText,
        timestamp: Date.now()
      });
    }
  };

  const handleToggleToVenues = () => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    console.log('🎯 Starting toggle animation: Community → Venues');
    console.log('🎯 Current activeButtonPosition:', activeButtonPositionRef.current._value);
    
    // Step 1: Animate the blue button sliding from Community (right) to Venues (left)
    Animated.timing(activeButtonPositionRef.current, {
      toValue: 0, // Move to left position (Venues)
      duration: 400,
      easing: Easing.bezier(0.25, 0.46, 0.45, 0.94), // Smooth ease-out
      useNativeDriver: false, // We need to animate layout properties
    }).start(() => {
      console.log('🎯 Blue indicator slide completed, starting fade down');
      console.log('🎯 Final activeButtonPosition:', activeButtonPositionRef.current._value);
      // Step 2: After button animation completes, fade everything down
      Animated.parallel([
        Animated.timing(toggleFadeRef.current, {
          toValue: 0,
          duration: 300,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(toggleSlideRef.current, {
          toValue: 30,
          duration: 300,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(subtitleFadeRef.current, {
          toValue: 0,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(subtitleSlideRef.current, {
          toValue: 30,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(searchFadeRef.current, {
          toValue: 0,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        }),
        Animated.timing(searchSlideRef.current, {
          toValue: 30,
          duration: 250,
          easing: Easing.bezier(0.4, 0, 0.2, 1),
          useNativeDriver: true
        })
      ]).start(() => {
        // Step 3: Navigate to Home after fade animation completes
        navigation.replace('Home');
      });
    });
  };

  const handleNearMe = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();
          
          // Format the location string properly
          const city = data.city || data.locality || '';
          const state = data.principalSubdivision || '';
          const searchString = `${city}, ${state}`.trim();
          
          setSearchText(searchString);
          
          // Navigate to location-based community search
          navigation.navigate('CommunityList', { 
            searchQuery: searchString,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            nearMe: true,
            timestamp: Date.now()
          });
        } catch (error) {
          console.error('Error getting location details:', error);
          alert('Error getting your location details. Please try again.');
        }
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Error accessing location. Please allow location access and try again.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: '#1075ae' }]}>
      <BackgroundVideo />
      <Animated.View 
        style={[
          globalStyles.overlay,
          { 
            backgroundColor: '#1075ae',
            opacity: 1,
            zIndex: 1,
            position: 'relative',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex'
          }
        ]}
      >
        <View style={[
          homeStyles.homeContent, 
          { 
            position: 'relative', 
            zIndex: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            maxWidth: 1200,
            paddingTop: 80,
            paddingBottom: 150,
          }
        ]}>
          <View style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: getResponsiveValue({
              mobile: spacing.xl,
              tablet: spacing.xxl,
              laptop: spacing.header,
              desktop: spacing.header
            }),
          }}>
            <Animated.Text 
              style={[
                homeStyles.homeSubtitle2,
                {
                  color: '#dfffdd',
                  opacity: subtitleFadeRef.current,
                  transform: [{ translateY: subtitleSlideRef.current }]
                },
                Platform.OS === 'web' && {
                  transition: 'transform 0.3s ease-in-out',
                  ':hover': {
                    transform: 'scale(1.02)',
                    textShadow: '0 0 10px rgba(255,255,255,0.5)',
                  }
                }
              ]}
            >
              Community Directory
            </Animated.Text>

            {!accessBlocked && (
            <Animated.View 
              style={[
                homeStyles.searchContainer,
                {
                  opacity: searchFadeRef.current,
                  transform: [{ translateY: searchSlideRef.current }],
                  marginTop: 40,
                }
              ]}
            >
              <SearchBar
                color="#ffeca8"
                value={searchText}
                onChangeText={setSearchText}
                onSubmit={handleSearch}
                placeholder={animatedPlaceholder}
              />
              <TouchableOpacity 
                style={[homeStyles.locationIconContainer, { color: '#ffeca8', stroke: '#ffeca8', fill: '#ffeca8' }]}
                onPress={handleNearMe}
                disabled={isGettingLocation}
              >
                {isGettingLocation ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <View style={homeStyles.svgIcon}>
                    <View style={[homeStyles.locationDot, { backgroundColor: '#ffeca8' }]} />
                    <View style={[homeStyles.locationRing, { borderColor: '#ffeca8' }]} />
                  </View>
                )}
              </TouchableOpacity>
            </Animated.View>
            )}

            {/* Page Toggle */}
            {!accessBlocked && (
            <Animated.View
              style={[
                {
                  opacity: toggleFadeRef.current || buttonsFadeRef.current,
                  transform: [
                    { translateY: toggleSlideRef.current }
                  ],
                  marginTop: 40,
                  backgroundColor: '#ffffff',
                  borderRadius: 25,
                  padding: 3,
                  borderWidth: 2,
                  borderColor: '#e0e0e0',
                  shadowColor: '#000',
                  shadowOffset: {
                    width: 0,
                    height: 4,
                  },
                  shadowOpacity: 0.15,
                  shadowRadius: 8,
                  elevation: 8,
                  position: 'relative',
                  width: 280,
                  height: 50,
                }
              ]}
            >
              {/* Animated Blue Background */}
              <Animated.View
                style={[
                  {
                    position: 'absolute',
                    top: 1,
                    width: 134, // Half of 280 - 6 (padding)
                    height: 44,
                    backgroundColor: '#4e7de0',
                    borderRadius: 22,
                    shadowColor: '#4e7de0',
                    shadowOffset: {
                      width: 0,
                      height: 2,
                    },
                    shadowOpacity: 0.3,
                    shadowRadius: 4,
                    elevation: 3,
                    left: activeButtonPositionRef.current.interpolate({
                      inputRange: [0, 1],
                      outputRange: [3, 143], // 3px to 143px (3 + 140)
                    }),
                  }
                ]}
              />
              
              {/* Button Labels */}
              <View style={{
                flexDirection: 'row',
                flex: 1,
              }}>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onPress={handleToggleToVenues}
                  disabled={isTransitioning}
                >
                  <Animated.Text style={{
                    fontSize: 16,
                    fontFamily: 'VastagoGrotesk',
                    fontWeight: '600',
                    color: activeButtonPositionRef.current.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['#ffffff', '#6c757d'], // White when active, gray when inactive
                    }),
                  }}>
                    Venues
                  </Animated.Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={{
                    flex: 1,
                    paddingHorizontal: 20,
                    paddingVertical: 12,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  disabled={isTransitioning}
                >
                  <Animated.Text style={{
                    fontSize: 16,
                    fontFamily: 'VastagoGrotesk',
                    fontWeight: '600',
                    color: activeButtonPositionRef.current.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['#6c757d', '#ffffff'], // Gray when inactive, white when active
                    }),
                  }}>
                    Community
                  </Animated.Text>
                </TouchableOpacity>
              </View>
            </Animated.View>
            )}

            
            {/* Rounded Auth Form */}
            {!accessBlocked && !userIsLoggedIn && (
              <Animated.View
                style={[
                  {
                    opacity: searchFadeRef.current,
                    transform: [{ translateY: searchSlideRef.current }],
                    marginTop: 40,
                    width: '100%',
                  }
                ]}
              >
                <CommunityAuthForm />
              </Animated.View>
            )}

          </View>
        </View>
        <StatusBar style="light" />
      </Animated.View>

      {/* Access Gate Popup */}
      <Modal
        visible={gateModalVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setGateModalVisible(false);
          navigation.replace('Home');
        }}
      >
        <View style={[globalStyles.container, { backgroundColor: 'rgba(0,0,0,0.7)', alignItems: 'center', justifyContent: 'center' }]}>
          <View style={{ backgroundColor: '#3D7FE6', borderRadius: 16, width: '90%', maxWidth: 480 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.2)' }}>
              <Text style={{ color: '#fff', fontSize: 20, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>{gateModalTitle}</Text>
              <TouchableOpacity onPress={() => { setGateModalVisible(false); navigation.replace('Home'); }}>
                <Text style={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>×</Text>
              </TouchableOpacity>
            </View>
            <View style={{ padding: 20 }}>
              <Text style={{ color: '#fff', fontSize: 16, fontFamily: 'VastagoGrotesk' }}>{gateModalMessage}</Text>
              <View style={{ marginTop: 16, alignItems: 'center' }}>
                <TouchableOpacity
                  style={{ backgroundColor: 'rgba(255,255,255,0.25)', paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20 }}
                  onPress={() => { setGateModalVisible(false); navigation.replace('Home'); }}
                >
                  <Text style={{ color: '#fff', fontSize: 14, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>OK</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
